/* ══════════════════════════════════════════════════════════════════════════
   HAVANI — FACETS

   Progressive enhancement over Shopify's native storefront filtering. The
   filter form is a real GET form to the collection URL, so it works with
   JavaScript off and every filter URL the live navigation already links —
   `?filter.p.m.custom.bangles=…`, `custom.metal`, `custom.color` — keeps
   working untouched. The names come from `collection.filters`, never from
   the theme, so no metafield is renamed or assumed here.

   With JavaScript on, the form is intercepted, the same URL is fetched, and
   only the grid and the facet rail are swapped in.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));

  class HvFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.debounce = null;

      /* ⚠️ SCOPE TO THE FACET FORM — `form` ALONE ADDED EVERY ITEM TWICE.

         This element wraps the WHOLE collection body, product grid included.
         Every product card carries its own real `<form action="/cart/add">`
         for quick add, so an unscoped listener here caught that submit as
         though it were a filter change: `apply()` built
         `/cart/add?form_type=product&id=…&quantity=1&section_id=…` from the
         card's own fields and `load()` GET it. Shopify treats that GET as an
         add to cart — so the item went in once from here and once from
         `hv-product-form`'s real POST, and the shopper got TWO.

         Measured before the fix on /collections/rings: 2 requests per click,
         `GET /cart/add?…quantity=1` from havani-facets `load()` and
         `POST /cart/add.js` from havani-theme `add()`, cart 2x. The homepage
         and the product page were unaffected — this file is only loaded by
         main-collection, which is why it looked like a collection-only fault.

         The same guard belongs on `change`: a quantity stepper inside the grid
         would otherwise re-run the filter query on every press. */
      this.addEventListener('change', (e) => {
        const form = e.target.closest && e.target.closest('form.facet-form');
        if (!form) return;
        /* price inputs get a beat; checkboxes go immediately */
        const wait = e.target.type === 'number' || e.target.type === 'text' ? 500 : 0;
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.apply(form), wait);
      });

      this.addEventListener('submit', (e) => {
        const form = e.target.closest && e.target.closest('form.facet-form');
        if (!form) return;
        e.preventDefault();
        this.apply(form);
      });

      /* active filter chips and "clear all" are plain links — intercept them
         so they behave like the rest of the form */
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (!link) return;
        e.preventDefault();
        this.load(link.href);
      });

      /* sort is its own tiny form */
      const sort = $('[data-sort-select]');
      if (sort) {
        sort.addEventListener('change', () => {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sort.value);
          url.searchParams.delete('page');
          this.load(url.toString());
        });
      }

      window.addEventListener('popstate', () => this.load(window.location.href, true));
    }

    apply(form) {
      if (!form) return;
      const params = new URLSearchParams(new FormData(form));
      /* drop empty values so the URL stays readable and shareable */
      Array.from(params.entries()).forEach(([key, value]) => {
        if (value === '') params.delete(key);
      });
      params.delete('page');
      const url = `${form.action.split('?')[0]}?${params.toString()}`;
      this.load(url);
    }

    async load(url, skipHistory) {
      const grid = $('[data-collection-grid]');
      grid?.classList.add('is-loading-grid');
      try {
        const res = await fetch(`${url}${url.indexOf('?') > -1 ? '&' : '?'}section_id=${this.sectionId}`);
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        ['[data-collection-grid]', '[data-facet-form]', '[data-facet-chips]', '[data-collection-count]', '[data-collection-pagination]']
          .forEach((sel) => {
            const fresh = parsed.querySelector(sel);
            const live = $(sel);
            if (fresh && live) live.innerHTML = fresh.innerHTML;
          });
        if (!skipHistory) window.history.pushState({}, '', url);
        const anchor = $('[data-collection-grid]');
        if (anchor && window.scrollY > anchor.offsetTop) {
          window.scrollTo({ top: anchor.offsetTop - 140, behavior: 'smooth' });
        }
      } catch (e) {
        window.location.href = url; /* fall back to a normal navigation */
      } finally {
        grid?.classList.remove('is-loading-grid');
      }
    }
  }
  if (!customElements.get('hv-facets')) customElements.define('hv-facets', HvFacets);
})();
